import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, FloppyDisk, ArrowsClockwise, Sparkle, MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SeoChecklist from '../components/SeoChecklist';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoSuggestions, setSeoSuggestions] = useState(null);
  const [seoBreakdown, setSeoBreakdown] = useState(null);
  const [readabilityScore, setReadabilityScore] = useState(null);
  const [showRawShort, setShowRawShort] = useState(false);
  const [showRawDesc, setShowRawDesc] = useState(false);
  const [researching, setResearching] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [wooOriginal, setWooOriginal] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [form, setForm] = useState({});
  // Forrige/Neste — based on the product list saved by ProductsList in
  // sessionStorage. Falls back to API-driven neighbour lookup if storage is
  // missing (e.g. user landed directly on /products/:id from a deep link).
  const [navState, setNavState] = useState({ prev: null, next: null, position: null, total: null });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // Compute Forrige/Neste navigation from the sessionStorage `productNav`
  // payload populated by ProductsList. Re-runs when the URL id changes (user
  // clicked Neste and the route updated).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('productNav');
      if (!raw) {
        setNavState({ prev: null, next: null, position: null, total: null });
        return;
      }
      const nav = JSON.parse(raw);
      const ids = nav.ids || [];
      const currentId = parseInt(id, 10);
      const idx = ids.indexOf(currentId);
      if (idx === -1) {
        setNavState({ prev: null, next: null, position: null, total: nav.total });
        return;
      }
      setNavState({
        prev: idx > 0 ? ids[idx - 1] : null,
        next: idx < ids.length - 1 ? ids[idx + 1] : null,
        position: idx + 1 + (nav.page > 1 ? (nav.page - 1) * 50 : 0),
        total: nav.total,
      });
    } catch {
      setNavState({ prev: null, next: null, position: null, total: null });
    }
  }, [id]);

  useEffect(() => {
    if (product) fetchSeoBreakdown();
  }, [product?.seo_score]);

  const fetchSeoBreakdown = async () => {
    const token = localStorage.getItem('token');
    try {
      // Pull both SEO and readability live, in parallel, so the top-level
      // "Google SEO Score" reflects the same numbers as the checklist below.
      // Combined score is 65% SEO + 35% Readability per user preference
      // (search-engine weight slightly higher than reading ease).
      const [seoRes, readRes] = await Promise.all([
        axios.get(`${API}/api/seo/score-breakdown/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/seo/readability/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setSeoBreakdown(seoRes.data);
      setReadabilityScore(readRes.data?.score ?? null);
    } catch {}
  };

  const fetchProduct = async () => {
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.get(`${API}/api/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProduct(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
        short_description: data.short_description || '',
        sku: data.sku || '',
        regular_price: data.regular_price || '',
        sale_price: data.sale_price || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        focus_keyword: data.focus_keyword || '',
      });
    } catch {
      toast.error('Kunne ikke hente produkt');
    } finally {
      setLoading(false);
    }
  };

  const saveProduct = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.put(`${API}/api/products/${id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      setProduct(data);
      toast.success('Produkt lagret lokalt');
    } catch {
      toast.error('Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  const pushToWoo = async () => {
    setPushing(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/sync/push/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produktet er pushet til WooCommerce');
      fetchProduct();
    } catch {
      toast.error('Kunne ikke pushe til WooCommerce');
    } finally {
      setPushing(false);
    }
  };

  const generateSeo = async () => {
    setSeoLoading(true);
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.post(`${API}/api/bulk/ai-process`, { 
        product_ids: [parseInt(id)], 
        actions: ['translate', 'seo', 'generate_text'] 
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (data.results?.[0]?.status === 'success') {
        toast.success('AI har oppdatert produktet! Se endringene nedenfor.');
        fetchProduct();
      } else {
        toast.error('AI-behandling feilet');
      }
    } catch {
      toast.error('Kunne ikke generere SEO-forslag');
    } finally {
      setSeoLoading(false);
    }
  };

  const researchProduct = async () => {
    setResearching(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/research/product/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Research startet — AI søker etter mer info om produktet');
    } catch {
      toast.error('Research feilet');
    } finally {
      setResearching(false);
    }
  };

  const showDiffView = async () => {
    setDiffLoading(true);
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.get(`${API}/api/products/${id}/woo-original`, { headers: { Authorization: `Bearer ${token}` } });
      setWooOriginal(data);
      setShowDiff(true);
    } catch {
      toast.error('Kunne ikke hente original fra WooCommerce');
    } finally {
      setDiffLoading(false);
    }
  };

  const applySeoSuggestion = async (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    toast.success(`${field} oppdatert med AI-forslag`);
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-8 w-64 bg-muted animate-pulse rounded" /><div className="h-96 bg-muted animate-pulse rounded-md" /></div>;
  }

  if (!product) {
    return <div className="text-center py-12 text-muted-foreground">Produkt ikke funnet</div>;
  }

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/products')} data-testid="back-to-products">
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl tracking-tight font-bold truncate">{product.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline">{product.type}</Badge>
              <Badge variant={product.status === 'publish' ? 'default' : 'secondary'}>{product.status}</Badge>
              {product.modified_locally && <Badge className="bg-warning/10 text-warning border-warning/20">Endret lokalt</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Forrige/Neste vare — only show if we have a list context from
              sessionStorage. Position-pill ("12 / 240") tells user where in
              the list they are. */}
          {(navState.prev || navState.next) && (
            <div className="flex items-center gap-1 mr-1" data-testid="product-nav">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navState.prev && navigate(`/products/${navState.prev}`)}
                disabled={!navState.prev}
                data-testid="prev-product-btn"
                title="Forrige vare"
                className="gap-1"
              >
                <CaretLeft size={14} weight="bold" />
                <span className="hidden sm:inline">Forrige</span>
              </Button>
              {navState.position != null && navState.total != null && (
                <span className="text-xs text-muted-foreground px-1 tabular-nums" data-testid="nav-position">
                  {navState.position}/{navState.total}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navState.next && navigate(`/products/${navState.next}`)}
                disabled={!navState.next}
                data-testid="next-product-btn"
                title="Neste vare"
                className="gap-1"
              >
                <span className="hidden sm:inline">Neste</span>
                <CaretRight size={14} weight="bold" />
              </Button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={researchProduct} disabled={researching} data-testid="research-button" className="gap-2">
            <MagnifyingGlass size={14} className={researching ? 'animate-spin' : ''} />
            {researching ? 'Søker...' : 'Research'}
          </Button>
          <Button variant="outline" size="sm" onClick={showDiffView} disabled={diffLoading} data-testid="diff-button" className="gap-2">
            {diffLoading ? 'Henter...' : 'Vis diff'}
          </Button>
          <Button variant="outline" size="sm" onClick={pushToWoo} disabled={pushing} data-testid="push-to-woo-button" className="gap-2">
            <ArrowsClockwise size={14} className={pushing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{pushing ? 'Pusher...' : 'Push til WooCommerce'}</span>
            <span className="sm:hidden">{pushing ? 'Pusher...' : 'Push'}</span>
          </Button>
          <Button size="sm" onClick={saveProduct} disabled={saving} data-testid="save-product-button" className="gap-2">
            <FloppyDisk size={14} />
            {saving ? 'Lagrer...' : 'Lagre'}
          </Button>
        </div>
      </div>

      {/* Diff View */}
      {showDiff && wooOriginal && (
        <Card className="border border-primary/30 bg-primary/5" data-testid="diff-view">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Sammenligning: WooCommerce vs. Lokalt</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDiff(false)}>Lukk</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {['name', 'short_description', 'description'].map(field => {
              const wooVal = wooOriginal[field] || '';
              const localVal = form[field] || '';
              const isDifferent = wooVal !== localVal;
              if (!isDifferent) return null;
              const labels = { name: 'Produktnavn', short_description: 'Kort beskrivelse', description: 'Produktbeskrivelse' };
              return (
                <div key={field} className="border border-border rounded-md overflow-hidden">
                  <div className="bg-muted/50 px-3 py-1.5 border-b border-border">
                    <span className="text-xs font-semibold">{labels[field]}</span>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-border">
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">WooCommerce (live)</p>
                      <div className="text-sm prose prose-sm max-w-none max-h-[200px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: wooVal || '<em class="text-muted-foreground">Tom</em>' }} />
                    </div>
                    <div className="p-3 bg-success/5">
                      <p className="text-[10px] uppercase tracking-wider text-success mb-1 font-semibold">Lokalt (nytt)</p>
                      <div className="text-sm prose prose-sm max-w-none max-h-[200px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: localVal || '<em class="text-muted-foreground">Tom</em>' }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {['name', 'short_description', 'description'].every(f => (wooOriginal[f] || '') === (form[f] || '')) && (
              <p className="text-sm text-muted-foreground text-center py-4">Ingen forskjeller — lokalt innhold er identisk med WooCommerce</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* SEO Score — live combined SEO (65%) + Readability (35%).
          Falls back to stored product.seo_score until breakdown loads, so the
          card never flashes "0/100" on first render.
          The "AI SEO-optimalisering"-button used to live here, but it duplicated
          the "Autofiks alt"-button in the checklist below. We removed it to
          force a single source of truth — Autofiks alt now does the same job. */}
      <Card className="border border-border" data-testid="seo-score-card">
        <CardContent className="p-4 flex items-center gap-4 sm:gap-6 flex-wrap">
          {(() => {
            const seo = seoBreakdown?.score;
            const read = readabilityScore;
            const combined =
              seo != null && read != null
                ? Math.round(seo * 0.65 + read * 0.35)
                : seo != null
                  ? seo
                  : (product.seo_score || 0);
            return (
              <>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Google SEO Score</p>
                  <p className="font-heading text-3xl font-bold">
                    {combined}<span className="text-lg text-muted-foreground">/100</span>
                  </p>
                  {seo != null && read != null && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      SEO {seo} × 65 % + Lesbarhet {read} × 35 %
                    </p>
                  )}
                </div>
                <Progress value={combined} className="flex-1 min-w-[120px] h-2" />
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* SEO + Readability checklist — single AI entry-point ("Autofiks alt").
          When user applies fixes, reload the combined score above. */}
      <SeoChecklist productId={product.id} onApplied={fetchSeoBreakdown} />

      {/* SEO Breakdown (legacy detailed table — kept as collapsed view) */}
      {seoBreakdown && (
        <details className="group">
          <summary className="text-xs text-muted-foreground hover:text-foreground cursor-pointer select-none">
            Vis detaljert poengfordeling
          </summary>
          <Card className="border border-border mt-2" data-testid="seo-breakdown-card">
            <CardContent className="space-y-2 pt-4">
              {seoBreakdown.breakdown?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{item.factor}</span>
                      <span className={`font-mono text-xs font-semibold ${item.score === item.max ? 'text-success' : item.score > 0 ? 'text-warning' : 'text-destructive'}`}>
                        {item.score}/{item.max}
                      </span>
                    </div>
                    {item.score < item.max && (
                      <p className="text-xs text-muted-foreground mt-0.5">{item.tip}</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </details>
      )}

      {/* Tabs */}
      <Tabs defaultValue="content" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max" data-testid="product-tabs">
            <TabsTrigger value="content">Innhold</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="history">Historikk</TabsTrigger>
            <TabsTrigger value="info">Info</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="content" className="space-y-4">
          <Card className="border border-border">
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label>Produktnavn</Label>
                <Input value={form.name} onChange={(e) => setForm(prev => ({...prev, name: e.target.value}))} data-testid="product-name-input" />
              </div>

              {/* Short Description - Visual + Edit */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kort beskrivelse</Label>
                  <button className="text-xs text-muted-foreground hover:text-primary" onClick={() => setShowRawShort(!showRawShort)}>
                    {showRawShort ? 'Vis forhåndsvisning' : 'Vis HTML-kode'}
                  </button>
                </div>
                {showRawShort ? (
                  <Textarea value={form.short_description} onChange={(e) => setForm(prev => ({...prev, short_description: e.target.value}))} rows={4} className="font-mono text-xs" data-testid="product-short-desc-input" />
                ) : (
                  <div className="border border-input rounded-md p-4 min-h-[80px] bg-background">
                    <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: form.short_description || '<span class="text-muted-foreground italic">Ingen kort beskrivelse</span>' }} />
                  </div>
                )}
              </div>

              {/* Description - Visual + Edit */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Produktbeskrivelse</Label>
                  <button className="text-xs text-muted-foreground hover:text-primary" onClick={() => setShowRawDesc(!showRawDesc)}>
                    {showRawDesc ? 'Vis forhåndsvisning' : 'Vis HTML-kode'}
                  </button>
                </div>
                {showRawDesc ? (
                  <Textarea value={form.description} onChange={(e) => setForm(prev => ({...prev, description: e.target.value}))} rows={10} className="font-mono text-xs" data-testid="product-desc-input" />
                ) : (
                  <div className="border border-input rounded-md p-4 min-h-[120px] bg-background">
                    <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: form.description || '<span class="text-muted-foreground italic">Ingen beskrivelse</span>' }} />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Input value={form.sku} onChange={(e) => setForm(prev => ({...prev, sku: e.target.value}))} data-testid="product-sku-input" />
                </div>
                <div className="space-y-2">
                  <Label>Pris</Label>
                  <Input value={form.regular_price} onChange={(e) => setForm(prev => ({...prev, regular_price: e.target.value}))} data-testid="product-price-input" />
                </div>
                <div className="space-y-2">
                  <Label>Salgspris</Label>
                  <Input value={form.sale_price} onChange={(e) => setForm(prev => ({...prev, sale_price: e.target.value}))} data-testid="product-sale-price-input" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seo" className="space-y-4">
          <Card className="border border-border">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Meta-tittel</Label>
                <Input value={form.meta_title} onChange={(e) => setForm(prev => ({...prev, meta_title: e.target.value}))} placeholder="SEO-tittel (maks 60 tegn)" data-testid="product-meta-title-input" />
                <p className="text-xs text-muted-foreground">{form.meta_title?.length || 0}/60 tegn</p>
              </div>
              <div className="space-y-2">
                <Label>Meta-beskrivelse</Label>
                <Textarea value={form.meta_description} onChange={(e) => setForm(prev => ({...prev, meta_description: e.target.value}))} placeholder="SEO-beskrivelse (maks 155 tegn)" rows={3} data-testid="product-meta-desc-input" />
                <p className="text-xs text-muted-foreground">{form.meta_description?.length || 0}/155 tegn</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Fokusnøkkelfrase
                  <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded">Yoast / Rank Math</span>
                </Label>
                <Input
                  value={form.focus_keyword || ''}
                  onChange={(e) => setForm(prev => ({...prev, focus_keyword: e.target.value}))}
                  placeholder="f.eks. rød ullgenser (1-4 ord)"
                  data-testid="product-focus-keyword-input"
                />
                <p className="text-xs text-muted-foreground">
                  Den sentrale søkefrasen Yoast bruker for å vurdere SEO-innhold. Bør forekomme i meta-tittel, meta-beskrivelse og produktbeskrivelse.
                </p>
              </div>

              {/* AI Suggestions */}
              {seoSuggestions && !seoSuggestions.error && (
                <div className="mt-6 space-y-4 border-t pt-4" data-testid="seo-suggestions">
                  <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                    <Sparkle size={14} className="text-primary" /> AI SEO-forslag
                  </h3>
                  
                  {seoSuggestions.meta_title && (
                    <div className="p-3 bg-muted/50 rounded-md flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Meta-tittel forslag:</p>
                        <p className="text-sm font-medium">{seoSuggestions.meta_title}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => applySeoSuggestion('meta_title', seoSuggestions.meta_title)} data-testid="apply-meta-title">Bruk</Button>
                    </div>
                  )}
                  
                  {seoSuggestions.meta_description && (
                    <div className="p-3 bg-muted/50 rounded-md flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Meta-beskrivelse forslag:</p>
                        <p className="text-sm">{seoSuggestions.meta_description}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => applySeoSuggestion('meta_description', seoSuggestions.meta_description)} data-testid="apply-meta-desc">Bruk</Button>
                    </div>
                  )}

                  {seoSuggestions.focus_keyword && (
                    <div className="p-3 bg-muted/50 rounded-md flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Fokusnøkkelfrase forslag:</p>
                        <p className="text-sm font-medium">{seoSuggestions.focus_keyword}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => applySeoSuggestion('focus_keyword', seoSuggestions.focus_keyword)} data-testid="apply-focus-keyword">Bruk</Button>
                    </div>
                  )}

                  {seoSuggestions.optimized_short_description && (
                    <div className="p-3 bg-muted/50 rounded-md flex items-center justify-between">
                      <div className="flex-1 mr-3">
                        <p className="text-xs text-muted-foreground">Kort beskrivelse forslag:</p>
                        <p className="text-sm">{seoSuggestions.optimized_short_description}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => applySeoSuggestion('short_description', seoSuggestions.optimized_short_description)} data-testid="apply-short-desc">Bruk</Button>
                    </div>
                  )}

                  {seoSuggestions.keywords?.length > 0 && (
                    <div className="p-3 bg-muted/50 rounded-md">
                      <p className="text-xs text-muted-foreground mb-2">Anbefalte nøkkelord:</p>
                      <div className="flex flex-wrap gap-1">
                        {seoSuggestions.keywords.map((kw, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {seoSuggestions.seo_tips?.length > 0 && (
                    <div className="p-3 bg-muted/50 rounded-md">
                      <p className="text-xs text-muted-foreground mb-2">SEO-tips:</p>
                      <ul className="space-y-1">
                        {seoSuggestions.seo_tips.map((tip, i) => (
                          <li key={i} className="text-sm text-muted-foreground">• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Produktbilder</CardTitle>
            </CardHeader>
            <CardContent>
              {product.images?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                  {product.images.map((img, i) => (
                    <div key={i} className="border border-border rounded-md overflow-hidden">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/proxy?url=${encodeURIComponent(img.src)}`} alt={img.alt || ''} className="w-full h-32 object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                      <p className="text-xs p-2 truncate text-muted-foreground">{img.name || `Bilde ${i+1}`}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-8 text-center">Ingen bilder knyttet til dette produktet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="info" className="space-y-4">
          <Card className="border border-border">
            <CardContent className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">WooCommerce ID:</span> <span className="font-mono">{product.id}</span></div>
                <div><span className="text-muted-foreground">Type:</span> {product.type}</div>
                <div><span className="text-muted-foreground">Status:</span> {product.status}</div>
                <div><span className="text-muted-foreground">Lager:</span> {product.stock_status} ({product.stock_quantity ?? 'N/A'})</div>
                <div><span className="text-muted-foreground">Nedlastbar:</span> {product.downloadable ? 'Ja' : 'Nei'}</div>
                <div><span className="text-muted-foreground">Kategorier:</span> {product.categories?.map(c => c.name).join(', ') || '-'}</div>
                <div><span className="text-muted-foreground">Opprettet:</span> {product.date_created ? new Date(product.date_created).toLocaleDateString('nb-NO') : '-'}</div>
                <div><span className="text-muted-foreground">Sist endret:</span> {product.date_modified ? new Date(product.date_modified).toLocaleDateString('nb-NO') : '-'}</div>
              </div>
              {product.permalink && (
                <a href={product.permalink} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                  Se i butikk &rarr;
                </a>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <HistoryTab productId={parseInt(id)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function HistoryTab({ productId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHistory(); }, [productId]);

  const fetchHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.get(`${API}/api/products/${productId}/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(data);
    } catch {} finally { setLoading(false); }
  };

  const revertTo = async (timestamp) => {
    if (!window.confirm('Tilbakestill til denne versjonen? Nåværende versjon lagres i historikken.')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/products/${productId}/revert`, { timestamp }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tilbakestilt! Last inn siden på nytt for å se endringene.');
      fetchHistory();
    } catch { toast.error('Kunne ikke tilbakestille'); }
  };

  if (loading) return <div className="h-32 bg-muted animate-pulse rounded-md" />;

  return (
    <Card className="border border-border" data-testid="history-tab">
      <CardHeader>
        <CardTitle className="font-heading text-lg">Endringshistorikk</CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Ingen historikk ennå</p>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {history.map((version, i) => (
              <div key={i} className="border border-border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(version.timestamp).toLocaleString('nb-NO')}
                      <Badge variant="outline" className="ml-2 text-xs">{version.source || 'manual'}</Badge>
                    </p>
                    <p className="text-sm">Endret: {version.changed_fields?.filter(f => !['modified_locally','local_modified_at','seo_score','_source'].includes(f)).join(', ')}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => revertTo(version.timestamp)} className="text-xs">
                    Tilbakestill
                  </Button>
                </div>
                {version.snapshot && Object.entries(version.snapshot).slice(0, 3).map(([key, val]) => (
                  <div key={key} className="text-xs mt-1">
                    <span className="text-muted-foreground">{key}:</span>{' '}
                    <span className="truncate inline-block max-w-[300px] align-bottom">{String(val).replace(/<[^>]*>/g, '').slice(0, 80)}{String(val).length > 80 ? '...' : ''}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
